import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Alert } from "@/components/ui";
import type { RendererProps } from "./types";

interface MaybeTopic {
  label?: string;
  summary?: string;
}

/** Fallback for interactive planning steps the web doesn't render first-class yet
 *  (de-trainer plan, tree checkup, trait plan). It shows the proposed
 *  plan read-only and points the user to the iOS app to finish — it never submits
 *  a guessed user_response (which would 422 against the part's strict schema). */
export function GenericPlanFallback({ step }: RendererProps) {
  const { t } = useTranslation();
  const output = step.output ?? {};
  const topics = Array.isArray((output as { topics?: unknown }).topics)
    ? ((output as { topics: MaybeTopic[] }).topics)
    : [];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold tracking-tight">{t("Eddi drafted a plan")}</h2>
      <Alert tone="info">
        {t("This planning step (")}<code className="font-mono text-xs">{step.part_slug}</code>{t(") isn't editable on the web yet — open this run in the EduPause iOS app to fine-tune and continue. Your draft is saved.")}
      </Alert>

      {topics.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-ink-soft">{t("Proposed topics")}</h3>
          {topics.map((t, i) => (
            <div key={i} className="card p-4">
              {t.label && <p className="font-semibold text-ink">{t.label}</p>}
              {t.summary && <p className="mt-0.5 text-sm text-ink-faint">{t.summary}</p>}
            </div>
          ))}
        </div>
      )}

      <Link to="/app/create" className="inline-block text-sm font-semibold text-brand-600 hover:underline">
        {t("← Back to Create")}
      </Link>
    </div>
  );
}
